import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import MemberLayout from "../components/MemberLayout";
import TrialBanner from "../components/TrialBanner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { firstNameOf } from "../lib/utils";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import UpgradeScreen from "../components/UpgradeScreen";
import { ChevronRight, Calendar, CheckCircle, Clock, AlertCircle, Play, X } from "lucide-react";
import { toast } from "sonner";

const VERTICAL_LABELS = {
  law_enforcement: "Law Enforcement", fire_service: "Fire Service", ems: "EMS",
  dispatch_psap: "Dispatch / PSAP", corrections: "Corrections", school_safety: "School Safety",
  military: "Military", emergency_medicine: "Emergency Medicine",
};

export default function MemberHome() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [modules, setModules] = useState([]);
  const [pwPromptDismissed, setPwPromptDismissed] = useState(
    () => localStorage.getItem("haruki_pw_prompt_dismissed") === "1"
  );
  const [showDemoNudge, setShowDemoNudge] = useState(false);
  useEffect(() => {
    if (user?.is_demo && localStorage.getItem("haruki_demo_run_first_scenario") === "1") {
      setShowDemoNudge(true);
    }
  }, [user]);
  const dismissDemoNudge = () => {
    try { localStorage.removeItem("haruki_demo_run_first_scenario"); }
    catch { /* localStorage unavailable — no-op */ }
    setShowDemoNudge(false);
  };
  const anyProgress = modules.some((m) => (m.scenarios_completed || 0) > 0);
  const showPwPrompt = user?.has_password === false && anyProgress && !pwPromptDismissed;

  useEffect(() => {
    api.get("/modules").then(r => {
      setModules(r.data);
      // Warm the scenario buffer for the module the user is most likely to open
      const target = r.data.find(m => (m.scenarios_completed || 0) < (m.total_scenarios || 5)) || r.data[0];
      if (target) api.post("/scenarios/warm", { module_type: target.module_type }).catch(() => {});
    }).catch(() => {});
  }, []);

  const start = async (mod) => {
    if (mod.completed_at) {
      toast.message("Module already complete. View your certificate from Certificates.");
      return;
    }
    if (!mod.available) {
      toast.message("This module hasn't been pushed yet.");
      return;
    }
    try {
      await api.post(`/modules/${mod.module_type}/start`);
      dismissDemoNudge();
      nav(`/app/scenario/${mod.module_type}`);
    } catch (e) {
      if (e?.response?.data?.detail?.code === "upgrade_required") {
        toast.error("Your trial has ended. Upgrade to continue training.");
      } else {
        toast.error("Could not start module.");
      }
    }
  };

  // Trial expired → upgrade experience (history & certificates stay reachable)
  if (user?.access_status === "trial_expired") {
    return (
      <MemberLayout>
        <UpgradeScreen />
      </MemberLayout>
    );
  }

  return (
    <MemberLayout>
      <div className="haruki-fade-in">
        <TrialBanner compact />
        <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {VERTICAL_LABELS[user?.vertical] || "Service Member"} · Training Platform
        </div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Hello, {firstNameOf(user)}.</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Training is available 24/7. Take a scenario when the day allows it — not on a schedule someone else set.
        </p>

        {showPwPrompt && (
          <div data-testid="password-prompt-card"
               className="mt-6 flex items-center justify-between gap-3 rounded-md border border-border px-4 py-3"
               style={{ background: "hsl(var(--secondary))" }}>
            <div className="text-sm">
              <span className="font-semibold">Add a backup way in.</span>{" "}
              <span className="text-muted-foreground">Set an optional password in case email is slow — sign-in links keep working either way.</span>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button asChild size="sm" variant="outline" data-testid="password-prompt-cta">
                <Link to="/app/settings">Set password</Link>
              </Button>
              <Button size="sm" variant="ghost" data-testid="password-prompt-dismiss"
                      onClick={() => { localStorage.setItem("haruki_pw_prompt_dismissed", "1"); setPwPromptDismissed(true); }}>
                Dismiss
              </Button>
            </div>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {showDemoNudge && (
            <div data-testid="demo-first-scenario-nudge"
                 className="flex items-start gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm">
              <div className="mt-0.5 rounded-md bg-amber-500/20 p-1.5">
                <Play className="h-3.5 w-3.5 text-amber-600" />
              </div>
              <div className="flex-1 text-amber-700 dark:text-amber-500">
                <div className="font-medium">Run your first scenario &rarr;</div>
                <div className="mt-0.5 text-xs opacity-80">
                  This is what your people see. Tap any available module below to try a real Haruki scenario end-to-end. Return to admin view any time from the banner above.
                </div>
              </div>
              <button data-testid="demo-first-scenario-nudge-dismiss"
                      onClick={dismissDemoNudge}
                      className="text-amber-600/70 hover:text-amber-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {modules.map((m) => {
            const status = m.completed_at
              ? "Completed"
              : m.started_at
                ? "In progress"
                : m.module_type === "critical_incident" && !m.available
                  ? "Not pushed"
                  : "Available";
            const StatusIcon = m.completed_at
              ? CheckCircle
              : status === "Available"
                ? Clock
                : AlertCircle;
            return (
              <Card key={m.module_type} className="overflow-hidden">
                <CardContent className="p-0">
                  <button
                    onClick={() => start(m)}
                    data-testid={`module-start-${m.module_type}`}
                    className="flex w-full items-center justify-between gap-4 p-5 text-left transition-opacity hover:opacity-90"
                  >
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <StatusIcon className="h-4 w-4" style={{ color: "hsl(var(--haruki-teal))" }} />
                        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{status}</span>
                      </div>
                      <div className="font-heading text-lg font-semibold">{m.title}</div>
                      <p className="text-sm text-muted-foreground">{m.description}</p>
                      <div className="flex items-center gap-4 pt-1 text-xs text-muted-foreground">
                        <span>{m.scenarios_completed} of {m.total_scenarios} scenarios</span>
                        {m.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> Due {m.due_date.slice(0, 10)}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </MemberLayout>
  );
}
